<script setup lang="ts">
    import {computed, nextTick, ref} from 'vue';
    import {createChatApi, type ActiveFilter, type ReturnLine} from './chatApi';
    import ChatActiveFilters from './ChatActiveFilters.vue';
    import ChatTranscript from './ChatTranscript.vue';
    import ChatWelcome from './ChatWelcome.vue';
    import {vehicleTitle} from './vehicleFormat';

    const props = defineProps<{
        conversationId: string;
        csrfToken: string;
        sendUrl: string;
        resetUrl: string;
        verifyReturnUrl: string;
        submitReturnUrl: string;
        verifyOrderStatusUrl: string;
        privacyUrl?: string;
        // Display only, forwarded to the welcome screen so it doesn't ask for a car the shop has
        // already got. The vehicle actually searched with is seeded server-side.
        carLabel?: string;
        carPlate?: string;
    }>();

    // The modal frame shows the resolved car as a kentekenchip in its header; it has no other way
    // to learn about it, because the conversation - not the browser session - owns the vehicle.
    const emit = defineEmits<{(event: 'vehicle', vehicle: Record<string, any> | null): void}>();

    const api = createChatApi({
        csrfToken: props.csrfToken,
        conversationId: props.conversationId,
        sendUrl: props.sendUrl,
        resetUrl: props.resetUrl,
        verifyReturnUrl: props.verifyReturnUrl,
        submitReturnUrl: props.submitReturnUrl,
        verifyOrderStatusUrl: props.verifyOrderStatusUrl,
    });

    type Entry = Record<string, any> & {id: number; kind: string; stale: boolean};

    /** Mirrors the max on SendChatMessageRequest, so the counter can't promise room the API refuses. */
    const MESSAGE_MAX_LENGTH = 2000;

    const entries = ref<Entry[]>([]);
    const pending = ref(false);
    const typing = ref(false);
    const draft = ref('');
    const draftInput = ref<HTMLInputElement | null>(null);
    const transcript = ref<HTMLElement | null>(null);
    const activeFilters = ref<ActiveFilter[]>([]);

    // A resolved vehicle is persistent chrome above the transcript, while an order card belongs
    // to the conversation. Both keys prevent repeated payloads from rendering duplicate state.
    let lastVehicleKey: string | null = null;
    let lastReturnEligibilityKey: string | null = null;
    let returnVerificationShown = false;
    let orderVerificationShown = false;
    let nextEntryId = 1;

    const INTERACTIVE = ['products', 'choices', 'plateSearch', 'vehicleCandidates', 'returnVerification', 'returnRequest', 'orderVerification'];

    const started = computed(() => entries.value.length > 0);
    const sendable = computed(() => !pending.value && draft.value.trim() !== '');
    const transcriptEntries = computed(() => (typing.value ? [...entries.value, {id: -1, kind: 'typing', stale: true}] : entries.value));

    function append(entry: Omit<Entry, 'id' | 'stale'>): Entry {
        const appended = {...entry, id: nextEntryId++, stale: false} as Entry;
        entries.value.push(appended);

        return appended;
    }

    function say(label: string, text: string, tone: 'me' | 'ai'): void {
        append({kind: 'message', label, text, tone});
    }

    // Errors use a separate tone so the bubble can render as a warning.
    function fail(text: string): void {
        append({kind: 'message', label: 'Fout', text, tone: 'error'});
    }

    // Disable earlier interactive entries when a new request starts.
    function disableStale(): number[] {
        const disabled = entries.value.filter((entry) => INTERACTIVE.includes(entry.kind) && !entry.stale);
        disabled.forEach((entry) => {
            entry.stale = true;
        });

        return disabled.map((entry) => entry.id);
    }

    function appendVehicle(vehicle: Record<string, any>): void {
        if (!vehicle || (!vehicle.carBrandName && !vehicle.modelName && !vehicle.typeName)) {
            return;
        }

        const key = [vehicle.typeCode, vehicle.carBrandName, vehicle.modelName, vehicle.typeName].filter(Boolean).join('|');
        if (key && key === lastVehicleKey) {
            return;
        }
        lastVehicleKey = key || lastVehicleKey;

        emit('vehicle', vehicle);
    }

    function appendReturnEligibility(eligibility: Record<string, any> | null | undefined): void {
        const orderLines = Array.isArray(eligibility?.orderLines) ? eligibility!.orderLines : [];
        const reasonOptions = Array.isArray(eligibility?.reasonOptions) ? eligibility!.reasonOptions : [];
        if (orderLines.length === 0 || reasonOptions.length === 0) {
            return;
        }

        const key = `${eligibility!.orderId}|${orderLines.map((line: Record<string, any>) => line.id).join(',')}`;
        if (key === lastReturnEligibilityKey) {
            return;
        }
        lastReturnEligibilityKey = key;

        append({kind: 'returnRequest', eligibility});
    }

    function appendReturnVerification(needed: boolean): void {
        if (!needed || returnVerificationShown) {
            return;
        }
        returnVerificationShown = true;

        append({kind: 'returnVerification'});
    }

    function appendOrderVerification(needed: boolean): void {
        if (!needed || orderVerificationShown) {
            return;
        }
        orderVerificationShown = true;

        append({kind: 'orderVerification'});
    }

    // Restore only controls disabled by this request if it fails.
    async function withPending(run: () => Promise<void>): Promise<void> {
        const disabled = disableStale();
        const responseStartId = nextEntryId;
        pending.value = true;
        typing.value = true;
        await nextTick();
        const messages = transcript.value?.querySelector<HTMLElement>('.messages');
        if (messages) {
            messages.scrollTo({top: messages.scrollHeight, behavior: 'instant'});
        }

        try {
            await run();
        } catch (error) {
            entries.value.forEach((entry) => {
                if (disabled.includes(entry.id)) {
                    entry.stale = false;
                }
            });
            fail((error as Error).message);
        } finally {
            typing.value = false;
            pending.value = false;
            // Disabling the input while pending force-blurs it (browsers can't keep a disabled
            // element focused) - nothing else restores focus once it re-enables, so the customer
            // has to click back into the field before they can type a reply.
            await nextTick();
            draftInput.value?.focus();
            await scrollToResponse(responseStartId);
        }
    }

    async function send(
        message: string,
        options: {
            choiceId?: string | null;
            displayMessage?: string;
            silent?: boolean;
        } = {}
    ): Promise<void> {
        // Form values stay visible in their widget and do not need a user bubble.
        if (!options.silent) {
            say('Jij', options.displayMessage ?? message, 'me');
        }

        await withPending(async () => {
            const payload = await api.send(message, options.choiceId ?? null);
            appendChatResponse(payload);
        });
    }

    function appendChatResponse(payload: Record<string, any>): void {
        activeFilters.value = Array.isArray(payload.activeFilters) ? payload.activeFilters : [];
        const hasOptions = Array.isArray(payload.options) && payload.options.length > 0;

        // The answer is one assistant turn. Any cards and controls that follow are standalone
        // parts of that answer and do not repeat the avatar or turn the facet prompt into a second
        // assistant message.
        if (payload.message) {
            say('PartBot', payload.message, 'ai');
        }

        appendVehicle(payload.vehicle || {});

        if (payload.needsVehiclePlate) {
            append({kind: 'plateSearch'});
        }

        if (Array.isArray(payload.products) && payload.products.length > 0) {
            append({kind: 'products', products: payload.products, meta: payload.productMeta || {}});
        }

        if (Array.isArray(payload.comparisonTable?.products) && payload.comparisonTable.products.length > 0) {
            append({kind: 'comparison', table: payload.comparisonTable});
        }

        if (Array.isArray(payload.vehicleCandidates) && payload.vehicleCandidates.length > 0) {
            append({kind: 'vehicleCandidates', candidates: payload.vehicleCandidates});
        }

        if (hasOptions) {
            append({
                kind: 'choices',
                labels: payload.options,
                counts: payload.optionCounts || {},
                logos: payload.optionLogos || [],
                choiceIds: payload.choiceIds || [],
            });
        }

        appendReturnEligibility(payload.returnEligibility);
        if (payload.orderStatus?.found) {
            append({kind: 'orderStatus', status: payload.orderStatus});
        }
        appendReturnVerification(payload.needsReturnVerification);
        appendOrderVerification(payload.needsOrderVerification);
    }

    async function removeFilter(filter: ActiveFilter): Promise<void> {
        await withPending(async () => {
            appendChatResponse(await api.removeFilter(filter));
        });
    }

    function submitDraft(): void {
        const message = draft.value.trim();
        if (message === '') {
            return;
        }

        draft.value = '';
        send(message);
    }

    // The plate remains visible in its widget.
    function searchPlate(plate: string): void {
        send(plate, {silent: true});
    }

    // Offered next to the plate field, same as searchPlate: the VIN itself becomes the message
    // text so Claude resolves it the same way it resolves a typed plate (core.md's
    // resolve_vehicle_by_vin instruction), rather than a separate non-conversational lookup path.
    function searchVin(vin: string): void {
        send(vin, {silent: true, displayMessage: `VIN-nummer ${vin}`});
    }

    // The comparison table itself is server-built (compare_products, see ChatComparisonTable.vue)
    // from the real product data - this message only needs to name the selected products so
    // Claude recognizes which ones and the intent to compare them.
    function compare(names: string[]): void {
        const list = names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} en ${names[names.length - 1]}`;

        send(`Vergelijk ${list}`, {
            displayMessage: `Vergelijk deze ${names.length} producten`,
        });
    }

    function choose(choice: {label: string; choiceId: string | null}): void {
        send(choice.label, {choiceId: choice.choiceId});
    }

    // The candidate is already fully resolved server-side (VehicleVinLookupTool::selectCandidate)
    // - the click just needs to name it back, the same "resend label + choiceId" round trip a
    // choice button uses, never a fresh model call.
    function chooseVehicleCandidate(candidate: Record<string, any>): void {
        const label = vehicleTitle(candidate, 'dit voertuig');
        send(`Dit is mijn voertuig: ${label}`, {choiceId: candidate.id});
    }

    // Verify structured credentials directly, outside the chat pipeline.
    async function verifyReturn(credentials: {orderId: string; emailAddress: string}): Promise<void> {
        await withPending(async () => {
            const payload = await api.verifyReturn(credentials.orderId, credentials.emailAddress);

            say('PartBot', payload.message, 'ai');
            appendReturnEligibility(payload.returnEligibility);

            if (payload.needsReturnVerification) {
                // Show a fresh form so the customer can retry verification.
                returnVerificationShown = false;
                appendReturnVerification(true);
            }
        });
    }

    async function verifyOrderStatus(credentials: {orderId: string; emailAddress: string}): Promise<void> {
        await withPending(async () => {
            const payload = await api.verifyOrderStatus(credentials.orderId, credentials.emailAddress);

            say('PartBot', payload.message, 'ai');
            if (payload.orderStatus?.found) {
                append({kind: 'orderStatus', status: payload.orderStatus});
            }

            if (payload.needsOrderVerification) {
                // Show a fresh form so the customer can retry verification.
                orderVerificationShown = false;
                appendOrderVerification(true);
            }
        });
    }

    async function submitReturn(orderLines: ReturnLine[]): Promise<void> {
        await withPending(async () => {
            say('PartBot', (await api.submitReturn(orderLines)).message, 'ai');
        });
    }

    function cancelReturn(): void {
        // Purely dismissing the form - nothing was submitted, so there is nothing for Claude to
        // process and no server round trip needed at all.
        disableStale();
        say('PartBot', 'Oké, de retour is geannuleerd.', 'ai');
    }

    async function reset(): Promise<void> {
        pending.value = true;

        try {
            const payload = await api.reset();

            // The welcome card stays put throughout the conversation; clearing the transcript is
            // itself the visible "this is a fresh chat" - no separate confirmation line needed.
            entries.value = [];
            lastVehicleKey = null;
            lastReturnEligibilityKey = null;
            returnVerificationShown = false;
            orderVerificationShown = false;
            activeFilters.value = [];
            draft.value = '';
            // The backend re-seeds the shop's currently-selected car the moment the conversation
            // resets (ConversationStore::seedCarOnce()) - without forwarding it here, the welcome
            // screen shown right after "Nieuwe chat" would show no car until the next message,
            // even though the shop still has one selected.
            emit('vehicle', payload.car ?? null);
        } catch (error) {
            fail((error as Error).message);
        } finally {
            pending.value = false;
        }
    }

    async function scrollToResponse(entryId: number): Promise<void> {
        await nextTick();
        const messages = transcript.value?.querySelector<HTMLElement>('.messages');
        const firstResponse = messages?.querySelector<HTMLElement>(`[data-entry-id="${entryId}"]`);
        if (!messages || !firstResponse) {
            return;
        }

        const styles = getComputedStyle(messages);
        const top =
            messages.scrollTop +
            firstResponse.getBoundingClientRect().top -
            messages.getBoundingClientRect().top -
            messages.clientTop -
            parseFloat(styles.paddingTop);

        // An answer shorter than the panel cannot reach the top on its own. Padding the transcript
        // out so it could left most of the panel blank now that the panel is tall, so the scroll is
        // left to clamp at the real end of the content: a short answer just follows its question.
        messages.scrollTo({
            top,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
        });
    }
</script>

<template>
    <div class="chat">
        <div
            ref="transcript"
            class="chat__transcript">
            <ChatTranscript
                :entries="transcriptEntries"
                @compare="compare"
                @choose="choose"
                @search-plate="searchPlate"
                @search-vin="searchVin"
                @select-vehicle-candidate="chooseVehicleCandidate"
                @verify="verifyReturn"
                @verify-order="verifyOrderStatus"
                @submit-return="submitReturn"
                @cancel-return="cancelReturn">
                <ChatWelcome
                    :disabled="pending"
                    :car-label="carLabel"
                    :car-plate="carPlate"
                    @search="searchPlate"
                    @search-vin="searchVin"
                    @ask="send" />
            </ChatTranscript>
        </div>

        <ChatActiveFilters
            v-if="activeFilters.length > 0"
            :filters="activeFilters"
            :disabled="pending"
            @remove="removeFilter" />

        <form
            class="composer"
            @submit.prevent="submitDraft">
            <div class="composer__row">
                <input
                    ref="draftInput"
                    v-model="draft"
                    class="form-control composer__input"
                    type="text"
                    :maxlength="MESSAGE_MAX_LENGTH"
                    :placeholder="started ? 'Typ je bericht…' : 'Stel je vraag over onderdelen, bestelling of retour…'"
                    :disabled="pending" />
                <button
                    type="submit"
                    class="c-button composer__send"
                    :disabled="!sendable"
                    :aria-label="pending ? 'Bezig…' : 'Verstuur'">
                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.4"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true">
                        <path d="M12 19V5" />
                        <path d="M5 12l7-7 7 7" />
                    </svg>
                </button>
            </div>

            <div class="composer__meta">
                <div class="composer__disclaimer text-muted">
                    Je praat met AI. Controleer belangrijke informatie en deel hier geen betaalgegevens.
                    <a
                        v-if="privacyUrl"
                        :href="privacyUrl"
                        target="_blank"
                        rel="noopener">
                        Privacybeleid
                    </a>
                </div>
                <div class="composer__aside">
                    <!-- Keep reset available on the standalone chat page. -->
                    <button
                        v-if="started"
                        type="button"
                        class="btn btn-link btn-xs"
                        :disabled="pending"
                        @click="reset">
                        Nieuwe chat
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<style scoped lang="scss">
    @import '../../sass/globals';
    .chat {
        display: flex;
        flex-direction: column;
        height: 100%;
        background: $white;
        color: $black;
        font-family: 'Source Sans Pro', 'Segoe UI', system-ui, sans-serif;
    }

    .chat__transcript {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        min-height: 0;
    }

    .composer {
        flex: 0 0 auto;
        padding: 14px 24px 16px;
    }

    .composer__row {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    .composer__input {
        flex: 1 1 auto;
        min-width: 0;
        height: 40px;
    }

    .composer__send {
        display: flex;
        flex: 0 0 40px;
        align-items: center;
        justify-content: center;
        width: 40px;
        padding: 0;
    }

    .composer__meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-top: 7px;
    }

    .composer__disclaimer {
        font-size: 12px;
        line-height: 1.4;
    }

    .composer__aside {
        display: flex;
        flex-shrink: 0;
        align-items: center;
        gap: 12px;
        font-size: 12px;
    }

    @media (max-width: $chat-mobile) {
        .composer {
            padding: 12px 16px 14px;
        }

        .composer__meta {
            flex-direction: column;
            align-items: flex-start;
        }
    }
</style>

<style lang="scss" src="../../sass/chat.scss"></style>
